import isString from 'lodash/isString';
import { CompanyIdentity } from '~/types/company-identity';
import { CompanySettings } from '~/types/company-settings';
import { User } from '~/types/user';
import { UserPersonalData } from '~/types/user-personal-data';
import { CountryIsoCode } from './country';
import { OutstandingCustomFiltersBlob } from './flow';
import { isDefined } from './typeGuards';

export interface UpdateOutstandingCustomFiltersRequest {
  id: string;
  outstandingCustomFilters: OutstandingCustomFiltersBlob;
}

export type OutstandingCustomFiltersData = Pick<CompanyUser, 'id' | 'outstandingCustomFilters'>;

export enum FiscalRegime {
  RF01 = 'RF01',
  RF02 = 'RF02',
  RF04 = 'RF04',
  RF05 = 'RF05',
  RF06 = 'RF06',
  RF07 = 'RF07',
  RF08 = 'RF08',
  RF09 = 'RF09',
  RF10 = 'RF10',
  RF11 = 'RF11',
  RF12 = 'RF12',
  RF13 = 'RF13',
  RF14 = 'RF14',
  RF15 = 'RF15',
  RF16 = 'RF16',
  RF17 = 'RF17',
  RF18 = 'RF18',
  RF19 = 'RF19',
  RF20 = 'RF20',
}

export interface LogoUrls {
  small: string | null;
  medium: string | null;
  large: string | null;
}

export interface Company {
  id: string;
  country: CountryIsoCode;
  name: string;
  taxNumber: string;
  vatNumber?: string;
  groupVatNumber: string | null;
  features: FeatureFlag[];
  featuresV2?: FeatureV2[];
  userRole: UserRole;
  fiscalRegime: FiscalRegime | null;
  logo: string | null;
  logoUrls: LogoUrls;
  companyIdentity: CompanyIdentity;
  leadSource?: LeadSource;
  companySettings?: CompanySettings | undefined;
  f24ConditionsAcceptedAt: string | null;
  f24SpecificClausesAcceptedAt: string | null;
}

export enum FeatureFlag {
  AutomaticCategorizationTransaction = 'AUTOMATIC_CATEGORIZATION_TRANSACTION',
  Treasury = 'TREASURY',
  Cashflow = 'CASHFLOW',
  CreditLines = 'CREDIT_LINES',
  SeeInvoices = 'SEE_INVOICES',
  Invoicing = 'INVOICING',
  Payments = 'PAYMENTS',
  AutomaticReconciliation = 'AUTOMATIC_RECONCILIATION',
  Outstanding = 'OUTSTANDING',
  SeeBalances = 'SEE_BALANCES',
  Bookkeeping = 'BOOKKEEPING',
  SplitTransactions = 'SPLIT_TRANSACTIONS',
  SplitConsent = 'SPLIT_CONSENT',
  SplitByCostCenter = 'SPLIT_BY_COST_CENTER',
  SibillCashback = 'SIBILL_CASHBACK',
  AutomaticOnboardingSecondCompanies = 'AUTOMATIC_ONBOARDING_SECOND_COMPANIES',
  AnalyticsDashboardsPurchases = 'ANALYTICS_DASHBOARDS_PURCHASES',
  AnalyticsDashboardsSales = 'ANALYTICS_DASHBOARDS_SALES',
  AnalyticsDashboardsBills = 'ANALYTICS_DASHBOARDS_BILLS',
  IntegrationApi = 'INTEGRATION_API',
  AiChat = 'AI_CHAT',
  NewAiVatProposals = 'NEW_AI_VAT_PROPOSALS',
  PayrollBookkeepingProposals = 'PAYROLL_BOOKKEEPING_PROPOSALS',
  BookkeepingIntrastat = 'BOOKKEEPING_INTRASTAT',
  SubscriptionManagement = 'SUBSCRIPTION_MANAGEMENT',
  F24Management = 'F24_MANAGEMENT',
  BilancioCee = 'BILANCIO_CEE',
}

export enum FeatureScope {
  Company = 'COMPANY',
  User = 'USER',
}

export interface FeatureV2 {
  value: FeatureFlag;
  scope: FeatureScope;
}

export enum UserRole {
  ENTREPRENEUR = 'ENTREPRENEUR',
  EMPLOYEE = 'EMPLOYEE',
  ADVISOR = 'ADVISOR',
  PAYROLL_CONSULTANT = 'PAYROLL_CONSULTANT',
}

export enum LeadSource {
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  FREE_INVOICING = 'FREE_INVOICING',
  SELF_SIGNUP = 'SELF_SIGNUP',
}

export const CompanyResourceType = 'company';

export const CompanyQueryKeys = {
  allCompanyUsers: ['CompanyUsers'],
  allCompanies: ['Company'],
  company: (companyId: string) => ['Company', companyId],
  companyUsers: (companyId: string) => ['CompanyUsers', companyId],
  companyUser: (companyId: string, userId: string) => ['CompanyUsers', companyId, userId],
  outstandingCustomFilters: (companyId: string, userId: string) => ['OutstandingCustomFilters', companyId, userId],
};

export interface CreateCompanyPayload {
  name?: string;
  taxNumber: string;
  vatNumber: string;
  userRole?: UserRole;
  fiscalRegime?: FiscalRegime;
  companyIdentity: { id: CompanyIdentity['id'] } | (Partial<Omit<CompanyIdentity, 'id'>> & { lid: string });
  leadSource?: LeadSource;
}

export enum CompanyUserStatus {
  Unauthorized = 'UNAUTHORIZED',
  Invited = 'INVITED',
  Authorized = 'AUTHORIZED',
}

export interface CompanyUser {
  id: string;
  features: FeatureFlag[];
  featuresV2?: FeatureV2[];
  role: UserRole;
  user: User;
  userPersonalData: UserPersonalData;
  status: CompanyUserStatus;
  consentUrl: string | null;
  aiTermsAcceptedAt: string | null;
  outstandingCustomFilters: OutstandingCustomFiltersBlob | null;
}

export const isCompanyLeadSource = (val: unknown): val is LeadSource => {
  if (!isDefined(val)) return false;
  if (!isString(val)) return false;
  return Object.values(LeadSource)
    .map((el) => el.toString())
    .includes(val);
};

export interface UserBankAccountPermissionsPayload {
  canViewAccount?: boolean;
  canInitiatePayments?: boolean;
  canManageBeneficiaries?: boolean;
  canManageAccountMembership?: boolean;
  canManageCards?: boolean;
}

export interface UpdateCompanyUserRequest {
  id: string;
  features?: FeatureFlag[];
  userBankAccountEnabled?: boolean;
  userBankAccountPermissions?: UserBankAccountPermissionsPayload;
  role?: UserRole;
  status?: CompanyUserStatus;
  aiTermsAcceptedAt?: string;
}

export interface InviteUserRequest {
  companyId: Company['id'];
  data: {
    email: string;
    features: FeatureFlag[];
    firstName: string;
    lastName: string;
    role: UserRole;
    cashbookInvitation?: boolean;
    userBankAccountEnabled?: boolean;
  };
}
